import type {
  AxiosError,
  AxiosProgressEvent,
  AxiosRequestConfig,
  RawAxiosRequestHeaders,
} from 'axios'
import type { PagedResults } from '@/types/paging/paged-results.model'
import type { PagingSortingFilteringDetails } from '@/types/paging/paging-sorting-filtering-details.model'
import axios, { HttpStatusCode } from 'axios'
import { useMapPagingSortingFilteringDetails } from '@/composables/useMapPagingSortingFilteringDetails'
import { useUserStore } from '@/stores/user.store'
import { HEADER_API_VERSION, HEADER_AUTHORIZATION, HEADER_CONTENT_TYPE, HEADER_ORG_ID, HEADER_TIMEZONE } from '@/utils/constants/headers'

const client = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
})

export async function postPagedRequest<T>(url: string, pagingSortingFilteringDetails: PagingSortingFilteringDetails, options?: AxiosRequestConfig): Promise<PagedResults<T>> {
  const mapped = useMapPagingSortingFilteringDetails(pagingSortingFilteringDetails)

  const response = await client.post(url, mapped, {
    ...(await getSharedAxiosRequestConfig()),
    ...options,
  })

  return response.data as PagedResults<T>
}

export async function getRequest<T>(url: string, options?: AxiosRequestConfig) {
  const sharedConfig = await getSharedAxiosRequestConfig()
  const response = await client.get(url, {
    ...sharedConfig,
    ...options,
    headers: {
      ...sharedConfig.headers,
      ...options?.headers,
    },
  })

  return response.data as T
}

export async function postRequest<TIn, TOut>(url: string, data: TIn, options?: AxiosRequestConfig) {
  const sharedConfig = await getSharedAxiosRequestConfig()
  const response = await client.post(url, data, {
    ...sharedConfig,
    ...options,
    headers: {
      ...sharedConfig.headers,
      ...options?.headers,
    },
  })

  return response.data as TOut
}

export async function putRequest<TIn, TOut>(url: string, data: TIn, options?: AxiosRequestConfig) {
  const response = await client.put(url, data, {
    ...(await getSharedAxiosRequestConfig()),
    ...options,
  })

  return response.data as TOut
}

export async function deleteRequest(url: string, options?: AxiosRequestConfig) {
  const response = await client.delete(url, {
    ...(await getSharedAxiosRequestConfig()),
    ...options,
  })

  return response.status === HttpStatusCode.Ok
}

export async function postDownloadRequest<TIn>(url: string, data: TIn, options?: AxiosRequestConfig, progressHandler?: ((loaded: number, total: number) => void) | undefined) {
  const response = await client.post(url, data, {
    ...(await getSharedAxiosRequestConfig()),
    ...options,
    onDownloadProgress(progressEvent: AxiosProgressEvent) {
      if (progressHandler && progressEvent.total) {
        progressHandler(progressEvent.loaded, progressEvent.total)
      }
    },
  })

  return response.data
}

export async function getDownloadRequest(url: string, options?: AxiosRequestConfig, progressHandler?: ((loaded: number, total: number) => void) | undefined) {
  const response = await client.get(url, {
    ...(await getSharedAxiosRequestConfig()),
    ...options,
    onDownloadProgress(progressEvent: AxiosProgressEvent) {
      if (progressHandler && progressEvent.total) {
        progressHandler(progressEvent.loaded, progressEvent.total)
      }
    },
  })

  return response.data
}

export async function postUploadRequest(url: string, file: File, options?: AxiosRequestConfig, errorHandler?: ((error: AxiosError) => AxiosError) | undefined) {
  await client
    .post(url, file, {
      ...(await getSharedAxiosRequestConfig(file.type)),
      ...options,
    })
    .catch(errorHandler)
}

async function getSharedAxiosRequestConfig(contentType?: string | undefined): Promise<AxiosRequestConfig> {
  const userStore = useUserStore()

  const requestHeaders: RawAxiosRequestHeaders = {}

  // Set the api-version header to 2.
  requestHeaders[HEADER_API_VERSION] = 2

  if (userStore.isLoggedIn()) {
    const userToken = await userStore.getUserToken()

    requestHeaders[HEADER_AUTHORIZATION] = `Bearer ${userToken}`

    if (contentType) {
      requestHeaders[HEADER_CONTENT_TYPE] = contentType
    }
  }

  if (userStore.organisationId) {
    // Set the org-id header to the users selected organisation id.
    requestHeaders[HEADER_ORG_ID] = userStore.organisationId
  }

  // Set timezone header using resolver.
  requestHeaders[HEADER_TIMEZONE] = resolveTimezoneHeader()

  return {
    headers: requestHeaders,
  } as AxiosRequestConfig
}

export function resolveTimezoneHeader(): string {
  const tz = getBrowserIanaTimezone()
  return tz || 'UTC'
}

function getBrowserIanaTimezone(): string | undefined {
  try {
    const tz = new Intl.DateTimeFormat().resolvedOptions().timeZone
    if (tz && typeof tz === 'string' && (tz === 'UTC' || tz.includes('/'))) {
      return tz
    }
  }
  catch {
    // ignore me
  }
  return undefined
}
